import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  ShieldCheck,
  Workflow,
  Lock,
  Smartphone,
  ArrowRight,
  Users,
  MessageSquareText,
  Clock,
  RefreshCw,
  MapPin,
  Sparkles,
  QrCode,
  Wand2,
} from "lucide-react";
import { BETA_MAILTO } from "@/components/Layout";
import { Brand } from "@/components/Logo";
import {
  PhoneFrame,
  HomeScreen,
  CardScreen,
  SyncScreen,
  IntrosScreen,
  THEMES,
} from "@/components/AppMock";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

// "Show, don't list" — one screen per outcome.
const SHOTS = [
  {
    eyebrow: "Always up to date",
    title: "Your address book fixes itself — with your permission.",
    body: "Two-way sync with the contacts you already have. peopleiknow brings in changes, sends yours out, and lets you review anything that clashes. Nothing changes without your OK.",
    Screen: SyncScreen,
    theme: THEMES.sepia,
    testId: "shot-sync",
  },
  {
    eyebrow: "Share the right you",
    title: "One card. Basic, Work, or Personal — you choose what they see.",
    body: "A digital business card with a QR code you can hand over in seconds. Pick which version each person gets, and keep the rest private.",
    Screen: CardScreen,
    theme: THEMES.ocean,
    testId: "shot-card",
  },
  {
    eyebrow: "Warm intros, less awkward",
    title: "Introduce two people you know in a couple of taps.",
    body: "Send two contacts each other's card, spin up an intro link, or run a group intro session where everyone swaps details with one QR.",
    Screen: IntrosScreen,
    theme: THEMES.indigo,
    testId: "shot-intros",
  },
];

// Extra capabilities spotted across the app.
const EXTRAS = [
  { icon: Wand2, title: "Auto name cleanups", body: "Fixes the messy, half-typed contact names hiding in your phone — and lets you review every edit." },
  { icon: Sparkles, title: "Ask peopleiknow", body: "A natural-language assistant that helps you find people and remember context, just by asking." },
  { icon: MapPin, title: "Places", body: "See where the people you know actually are — Calgary, Vancouver, Toronto, wherever life scattered them." },
  { icon: Users, title: "Groups", body: "Organize people into Swimmers, Family, Close friends — whatever groupings make sense to you." },
  { icon: RefreshCw, title: "Synced everywhere", body: "Your relationships stay in step across every device, updated in the background." },
  { icon: QrCode, title: "Group intro sessions", body: "Everyone at an event swaps cards with a single QR code. No more \"text me your number.\"" },
];

const SCENARIOS = [
  {
    icon: Users,
    title: "After a conference",
    body: "Remember how you met and what you said you'd follow up on — before the business cards turn to mush in your pocket.",
  },
  {
    icon: MessageSquareText,
    title: "Before reaching out",
    body: "See the last conversation and the context that matters, so your message feels personal instead of generic.",
  },
  {
    icon: Clock,
    title: "When life gets busy",
    body: "Reconnect before important relationships quietly drift — a gentle nudge instead of a guilty \"it's been too long.\"",
  },
];

const TRUST = [
  {
    icon: ShieldCheck,
    title: "Privacy-first mindset",
    body: "Built from the ground up to respect your data. We don't sell personal data, and we don't use it for advertising.",
  },
  {
    icon: Workflow,
    title: "Friction-free by design",
    body: "Relationship management should feel simple and human — not like another chore in another dashboard.",
  },
  {
    icon: Lock,
    title: "Responsible Google handling",
    body: "Where Google-connected features are enabled, data is handled per our published privacy and data-use policies.",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white border-b border-slate-200">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-16 sm:pb-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial="hidden"
              animate="show"
              variants={{ show: { transition: { staggerChildren: 0.12 } } }}
            >
              <motion.div variants={fadeUp}>
                <span
                  className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3.5 py-1.5 text-xs sm:text-sm font-medium text-amber-700"
                  data-testid="hero-beta-badge"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                  In active development &amp; beta testing
                </span>
              </motion.div>

              <motion.h1
                variants={fadeUp}
                className="mt-6 text-4xl sm:text-5xl lg:text-[3.4rem] font-bold tracking-tight leading-[1.06] text-slate-900"
              >
                Your Contacts app remembers phone numbers.{" "}
                <span className="text-blue-700">
                  <Brand /> remembers people.
                </span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl"
              >
                <Brand /> is a private relationship manager that syncs with the
                contacts you already have — then helps you remember the details,
                stay in touch, and show up thoughtfully for the people you know,
                personal and professional.
              </motion.p>

              <motion.div
                variants={fadeUp}
                className="mt-8 flex flex-col sm:flex-row gap-3"
              >
                <a
                  href="https://app.peopleiknow.ai/signup"
                  data-testid="hero-signup-cta"
                  className="inline-flex items-center justify-center gap-2 bg-blue-700 text-white rounded-lg px-6 py-3.5 text-base font-semibold hover:bg-blue-800 transition-colors shadow-sm active:scale-95"
                >
                  Sign up for free
                </a>
                <a
                  href="https://app.peopleiknow.ai"
                  data-testid="hero-signin-cta"
                  className="inline-flex items-center justify-center gap-2 bg-white text-slate-700 border border-slate-200 rounded-lg px-6 py-3.5 text-base font-medium hover:bg-slate-50 transition-colors shadow-sm active:scale-95"
                >
                  Sign in
                </a>
              </motion.div>
              <motion.div variants={fadeUp} className="mt-4">
                <Link
                  to="/privacy"
                  data-testid="hero-privacy-cta"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-blue-700 transition-colors"
                >
                  Read about privacy
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
              className="flex justify-center"
            >
              <PhoneFrame testId="hero-phone">
                <HomeScreen theme={THEMES.ocean} />
              </PhoneFrame>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HUMOR / PROBLEM */}
      <section className="bg-slate-900 text-white" data-testid="problem-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <div className="flex items-center gap-3 text-slate-400">
            <Smartphone className="h-5 w-5" />
            <span className="text-sm font-medium uppercase tracking-widest">
              Let's be honest
            </span>
          </div>
          <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]">
            The contacts app on your phone is basically a haunted address book.
          </h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 text-slate-300 text-base leading-relaxed">
            <p>
              It's ugly, it never updates itself, and it's quietly full of bad
              data — three numbers for one person and contacts like{" "}
              <span className="text-white font-medium">"John (conference??)"</span>{" "}
              and{" "}
              <span className="text-white font-medium">"Do NOT answer — Steve."</span>
            </p>
            <p>
              So when someone you actually want to hear from texts, you're
              squinting at a gray bubble trying to remember who they are.
            </p>
          </div>
          <p className="mt-10 text-lg sm:text-xl font-medium text-white max-w-3xl">
            <Brand dark /> helps you keep track of the people you know in a way
            that feels human, not transactional — so it's easier to remember the
            details, stay in touch, and build better long-term relationships.
          </p>
        </div>
      </section>

      {/* SHOW, DON'T LIST */}
      <section className="bg-[#f8fafc]" data-testid="show-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <div className="max-w-2xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-blue-700">
              Context is captured
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              "You added a new contact. Who was that again?"
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
              In your contacts app all you have is a long list. <Brand /> adds
              context:{" "}
              <span className="text-blue-700 font-semibold">
                met on Aug 27, 2026 at Piccadilly Circus, London UK.
              </span>{" "}
              and everything is searchable.
            </p>
          </div>

          <div className="mt-14 space-y-20 sm:space-y-24">
            {SHOTS.map((shot, i) => {
              const reverse = i % 2 === 1;
              const Screen = shot.Screen;
              return (
                <motion.div
                  key={shot.testId}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center"
                >
                  <div className={reverse ? "lg:order-2" : ""}>
                    <span className="text-xs font-semibold uppercase tracking-widest text-blue-700">
                      {shot.eyebrow}
                    </span>
                    <h3 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                      {shot.title}
                    </h3>
                    <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-lg">
                      {shot.body}
                    </p>
                  </div>
                  <div className={reverse ? "lg:order-1" : ""}>
                    <PhoneFrame testId={shot.testId}>
                      <Screen theme={shot.theme} />
                    </PhoneFrame>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COLOR THEMES */}
      <section className="bg-white border-t border-slate-200 overflow-hidden" data-testid="themes-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <div className="max-w-2xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-blue-700">
              Make it yours
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Pick a look that feels like you.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
              Switch color themes any time — from calm ocean blues to warm sepia,
              soft rose, or a deep indigo dark mode. Same app, your vibe.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-8 items-start">
            {[THEMES.rose, THEMES.sepia, THEMES.indigo].map((theme) => (
              <motion.div
                key={theme.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col items-center gap-4"
              >
                <PhoneFrame className="max-w-[230px]" testId={`theme-phone-${theme.key}`}>
                  <HomeScreen theme={theme} />
                </PhoneFrame>
                <span className="text-sm font-semibold text-slate-500">
                  {theme.name}
                  {theme.dark ? " · dark" : ""}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* EXTRA FEATURES */}
      <section className="bg-[#f8fafc] border-t border-slate-200" data-testid="extras-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <div className="max-w-2xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-blue-700">
              And a few more things you'll like
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Little touches that make it feel effortless.
            </h2>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {EXTRAS.map((e) => {
              const Icon = e.icon;
              return (
                <div
                  key={e.title}
                  className="bg-white rounded-2xl border border-slate-200 p-7"
                  data-testid={`extra-${e.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-slate-900">{e.title}</h3>
                  <p className="mt-2 text-[15px] text-slate-600 leading-relaxed">{e.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHO IT IS FOR */}
      <section className="bg-white border-t border-slate-200" data-testid="scenarios-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <div className="max-w-2xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-blue-700">
              Who it's for
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Made for the moments that matter.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
              Not a rolodex, not a sales tool — just a better way to stay close to
              the people you know.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {SCENARIOS.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  className="bg-[#f8fafc] rounded-2xl border border-slate-200 p-8"
                  data-testid={`scenario-${s.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-white border border-slate-200 text-blue-700 flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-slate-900">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-[15px] text-slate-600 leading-relaxed">
                    {s.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BETA NOTICE */}
      <section className="bg-white" data-testid="beta-notice-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-20 sm:pb-28">
          <div className="rounded-3xl bg-gradient-to-br from-blue-700 to-blue-900 text-white p-10 sm:p-14 relative overflow-hidden">
            <div className="grain-overlay absolute inset-0 pointer-events-none" />
            <div className="relative max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-300 animate-pulse" />
                Beta program
              </span>
              <h2 className="mt-5 text-2xl sm:text-3xl font-bold tracking-tight">
                <Brand dark /> is in active development and beta testing.
              </h2>
              <p className="mt-4 text-blue-100 leading-relaxed">
                We're working with a small number of early users to refine the
                product experience. If that sounds like you, we'd love to have you
                in the room while we build.
              </p>
              <a
                href={BETA_MAILTO}
                data-testid="beta-notice-cta"
                className="mt-7 inline-flex items-center gap-2 bg-white text-blue-800 rounded-lg px-6 py-3.5 text-base font-semibold hover:bg-blue-50 transition-colors active:scale-95"
              >
                <Mail className="h-5 w-5" />
                Request early access
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-[#f8fafc] border-t border-slate-200" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <div className="max-w-2xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-blue-700">
              Built on trust
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Private relationship intelligence — emphasis on private.
            </h2>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {TRUST.map((t) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.title}
                  className="bg-white rounded-2xl border border-slate-200 p-8"
                  data-testid={`trust-card-${t.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-slate-900">
                    {t.title}
                  </h3>
                  <p className="mt-2 text-[15px] text-slate-600 leading-relaxed">
                    {t.body}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-10 flex flex-wrap gap-4 text-sm">
            <Link
              to="/privacy"
              data-testid="trust-privacy-link"
              className="font-semibold text-blue-700 hover:text-blue-800 transition-colors inline-flex items-center gap-1"
            >
              Read our Privacy Policy <ArrowRight className="h-4 w-4" />
            </Link>
            <span className="text-slate-300">•</span>
            <Link
              to="/terms"
              data-testid="trust-terms-link"
              className="font-semibold text-blue-700 hover:text-blue-800 transition-colors inline-flex items-center gap-1"
            >
              Read our Terms <ArrowRight className="h-4 w-4" />
            </Link>
            <span className="text-slate-300">•</span>
            <Link
              to="/security"
              data-testid="trust-security-link"
              className="font-semibold text-blue-700 hover:text-blue-800 transition-colors inline-flex items-center gap-1"
            >
              How we handle security <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
